import {computed, nextTick, ref, Ref} from "vue";
import Translator from "@/utils/common/Translator.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {
  ChatAttachment,
  ChatMessagePayload,
  ChatMetadata,
  ChatUnreadCountPayload,
  MessageMetadata,
  MessageStatusUpdate,
  PresenceUpdate,
  WsChatMessagePayload,
  WsPresencePayload,
  WsStatusUpdatePayload,
  WsTypingPayload
} from "@/utils/presenters/interfaces.ts";
import {BroadcastMessageType, WebSocketMessageType} from "@/utils/UIConstants/enums.ts";
import ChatMessageStatus from "@/utils/constants/ChatMessageStatus.ts";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import {namaBroadcast} from "@/utils/broadcast/NamaBroadcastChannel.ts";
import {namaWebSocket} from "@/utils/broadcast/NamaWebSocketManager.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import {type SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import DTOChatMessage = namaTypes.DTOChatMessage;
import DTOUser = namaTypes.DTOUser;
import DTOChatConversation = namaTypes.DTOChatConversation;
import ChatConversationRequest = namaTypes.ChatConversationRequest;
import DTOChatParticipant = namaTypes.DTOChatParticipant;
import MarkConversationReadRequest = namaTypes.MarkConversationReadRequest;
import TypingIndicatorDTO = namaTypes.TypingIndicatorDTO;
import DTOLargeData = namaTypes.DTOLargeData;
import EntityReferenceData = namaTypes.EntityReferenceData;
import ChatMessagePageResponse = namaTypes.ChatMessagePageResponse;
import ChatMessageSearchResult = namaTypes.ChatMessageSearchResult;

export default class ChatPresenter extends AbsPresenter {
  messageMetadataList = ref<MessageMetadata[]>([]);
  chatMetadataList = ref<ChatMetadata[]>([]);
  currentChatMetadata = ref<ChatMetadata>();
  messageText = ref<string>('');
  isCreating = ref(false);
  onlineUsersMap = ref<Map<string, boolean>>(new Map());
  otherUserTyping = ref(false);
  typingTimeout: any = null;
  draftMessages: Record<string, string> = {};
  private wsUnsubscribers: (() => void)[] = [];
  private broadcastUnsubscribers: (() => void)[] = [];
  // Track processed message IDs to prevent duplicate handling
  private processedMessageIds: Set<string> = new Set();
  currentUserInfo: DTOUser | undefined = LoginUtils.currentUserInfo();
  unreadCount = ref(0);
  chatContainerRef: Ref<HTMLElement | null> = ref(null);
  hasMoreMessages = ref(true);
  hasMoreAfter = ref(false);
  isLoadingMore = ref(false);
  isLoadingMoreAfter = ref(false);
  initialLoadDone = ref(false);
  isAutoScrolling = ref(false);
  private static readonly PAGE_SIZE = 20;
  // Mention detection state
  mentionActive = ref(false);
  mentionQuery = ref('');
  mentionEntityId = ref<string | null>(null);
  mentionRange = ref<Range | null>(null);
  private mentionEscaped = false;

  constructor() {
    super(undefined)
    this.setupWebSocketListeners();
    this.setupBroadcastListeners();
  }

  /**
   * Get connection status from shared WebSocket manager
   */
  get connected(): Ref<boolean> {
    return namaWebSocket.getConnectionStatus();
  }

  /**
   * Setup WebSocket event listeners via the shared manager
   */
  private setupWebSocketListeners(): void {
    // Listen for incoming chat messages
    const unsubMessage = namaWebSocket.on<WsChatMessagePayload>(
      WebSocketMessageType.WS_CHAT_MESSAGE,
      async (payload) => {
        const message = this.createMessageMetadataFromDTO(payload.message);
        await this.handleIncomingMessage(message);
      }
    );
    this.wsUnsubscribers.push(unsubMessage);
    // Listen for message status updates
    const unsubStatus = namaWebSocket.on<WsStatusUpdatePayload>(
      WebSocketMessageType.WS_MESSAGE_STATUS,
      (payload) => {
        this.handleMessageStatusUpdate(payload.statusUpdate);
      }
    );
    this.wsUnsubscribers.push(unsubStatus);
    // Listen for presence updates
    const unsubPresence = namaWebSocket.on<WsPresencePayload>(
      WebSocketMessageType.WS_PRESENCE_UPDATE,
      (payload) => {
        this.handlePresenceUpdate(payload.presenceUpdate);
      }
    );
    this.wsUnsubscribers.push(unsubPresence);
    // Listen for typing indicators
    const unsubTyping = namaWebSocket.on<WsTypingPayload>(
      WebSocketMessageType.WS_TYPING_INDICATOR,
      (payload) => {
        if (payload.indicator.conversationId === this.currentChatMetadata.value?.id) {
          this.handleTypingIndicator(payload.indicator);
        }
      }
    );
    this.wsUnsubscribers.push(unsubTyping);
  }

  /**
   * Setup BroadcastChannel listeners for cross-tab sync
   */
  private setupBroadcastListeners() {
    // Listen for chat messages from other tabs
    const unsubMessage = namaBroadcast.on<ChatMessagePayload>(
      BroadcastMessageType.CHAT_NEW_MESSAGE,
      async (payload) => {
        await this.handleCrossTabMessage(payload);
      }
    );
    this.broadcastUnsubscribers.push(unsubMessage);
    // Listen for presence updates from other tabs
    const unsubPresence = namaBroadcast.on<PresenceUpdate>(
      BroadcastMessageType.CHAT_PRESENCE,
      (payload) => {
        this.onlineUsersMap.value.set(payload.userId, payload.online);
      }
    );
    this.broadcastUnsubscribers.push(unsubPresence);
    // Listen for unread count updates from other tabs
    const unsubUnread = namaBroadcast.on<ChatUnreadCountPayload>(
      BroadcastMessageType.CHAT_UNREAD_COUNT,
      (payload) => {
        this.handleCrossTabUnreadCount(payload);
      }
    );
    this.broadcastUnsubscribers.push(unsubUnread);
  }

  /**
   * Handle chat message received from another tab
   */
  private async handleCrossTabMessage(payload: ChatMessagePayload) {
    const message = payload.message;
    if (message.conversationId !== this.currentChatMetadata.value?.id) {
      await this.ensureConversationInList(message.conversationId);
      const chatMetadata = this.chatMetadataList.value.find(c => c.id === message.conversationId);
      if (chatMetadata) {
        chatMetadata.lastMessagePreview = ChatUtils.getMessagePreviewText(message);
        chatMetadata.lastMessageType = message.messageType;
        chatMetadata.lastMessageAt = message.sentAt;
      }
    } else {
      const existingIndex = this.messageMetadataList.value.findIndex(m => m.id === message.id);
      if (existingIndex === -1 && message.sender?.id !== this.currentUserInfo?.id && !this.hasMoreAfter.value) {
        this.messageMetadataList.value.push(message);
        await this.scrollToBottom();
      }
    }
  }

  /**
   * Handle unread count update from another tab
   */
  private handleCrossTabUnreadCount(payload: ChatUnreadCountPayload): void {
    const chatMetadata = this.chatMetadataList.value.find(c => c.id === payload.conversationId);
    if (chatMetadata) {
      chatMetadata.unreadMessagesCount = payload.count;
    }
  }

  async start() {
    await this.fetchConversations();
  }

  messageTextComputed() {
    return computed(() => this.messageText.value);
  }

  private async fetchConversations() {
    let response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION, {
      method: "GET",
      params: {userId: this.currentUserInfo?.id, userType: 'user'},
      doNotUseMainReqForHttp: true
    });
    if (response && NamaCommon.isSuccess(response)) {
      await this.preloadConversationMetadata(response);
    }
  }

  private async preloadConversationMetadata(chatConversations: DTOChatConversation[] | undefined) {
    if (!chatConversations || chatConversations.length < 1)
      return;
    for (const chat of chatConversations) {
      if (chat.id) {
        let chatParticipants: DTOChatParticipant[] | undefined = await this.fetchChatParticipants(chat.id);
        this.chatMetadataList.value.push(this.buildChatMetadata(chat, chatParticipants ?? []));
      }
    }
    await this.loadUnreadCounts();
  }

  private async loadUnreadCounts() {
    try {
      const response = await NamaCommon.namaFetch(NamaURLS.UNREAD_BY_CONVERSATION, {
        method: "GET",
        params: {userId: this.currentUserInfo?.id, userType: 'user'},
        doNotUseMainReqForHttp: true,
        noLoadingIndicator: true
      });
      if (response && NamaCommon.isSuccess(response)) {
        for (const item of response) {
          const chatMetadata = this.chatMetadataList.value.find(c => c.id === item.conversationId);
          if (chatMetadata) {
            chatMetadata.unreadMessagesCount = item.unreadCount;
          }
        }
      }
    } catch (error) {
      console.error('Failed to load unread counts:', error);
    }
  }

  private buildChatMetadata(chat: DTOChatConversation, participants?: DTOChatParticipant[]): ChatMetadata {
    return {
      id: chat.id,
      chatTitle: this.fetchChatTitle(chat, participants),
      groupTitle: chat.groupTitle,
      chatParticipantsTitle: this.fetchChatParticipantsAsTitle(participants, chat),
      imageSrc: this.fetchChatImage(chat, participants),
      participants: participants,
      chatConversationType: chat.chatConversationType,
      isActive: chat.isActive,
      lastMessageAt: chat.lastMessageAt,
      lastMessagePreview: ChatUtils.getLastMessagePreview(chat, chat.currentUserRole, this.currentUserInfo?.id),
      lastMessageType: chat.lastMessageType,
      unreadMessagesCount: this.calculateUnreadCount(participants),
      createdAt: chat.createdAt,
      groupOwner: chat.groupOwner,
      participantCount: chat.participantCount,
      updatedAt: chat.updatedAt,
      currentUserRole: chat.currentUserRole,
      currentUserRemovedAt: chat.currentUserRemovedAt,
    };
  }

  private calculateUnreadCount(participants: DTOChatParticipant[] | undefined): number {
    if (!participants || !participants.length)
      return 0;
    const currentUserParticipant = participants.find(p => p.participant?.id === this.currentUserInfo?.id);
    return currentUserParticipant?.unreadMessagesCount ?? 0;
  }

  private resetUnreadCount(chatId: string | undefined) {
    if (!chatId)
      return;
    const chatMetadata = this.chatMetadataList.value.find(c => c.id === chatId);
    if (chatMetadata) {
      chatMetadata.unreadMessagesCount = 0;
      // Also update the participant's unread count
      const currentUserParticipant = chatMetadata.participants?.find(
        p => p.participant?.id === this.currentUserInfo?.id
      );
      if (currentUserParticipant) {
        currentUserParticipant.unreadMessagesCount = 0;
      }
    }
  }

  async fetchChatAndOpen(chat: ChatMetadata, snippetSendAt?: Date) {
    if (!chat || !chat.id)
      return;
    this.initialLoadDone.value = false;
    this.saveCurrentDraft();
    if (this.currentChatMetadata.value?.id) {
      namaWebSocket.unsubscribeFromTyping(this.currentChatMetadata.value.id);
    }
    this.currentChatMetadata.value = chat;
    this.currentChatMetadata.value.participants = await this.fetchChatParticipants(chat.id);
    const otherParticipant = this.currentChatMetadata.value.participants?.find(
      p => p.participant?.id !== this.currentUserInfo?.id
    );
    this.currentChatMetadata.value.imageSrc = this.fetchChatImage(chat, this.currentChatMetadata.value.participants);
    this.currentChatMetadata.value.chatTitle = this.fetchChatTitle(chat, this.currentChatMetadata.value.participants);
    await this.fetchChatMessages(chat.id, snippetSendAt);
    this.messageText.value = this.getDraftMessage(chat.id);
    // Subscribe to typing indicators for this conversation
    namaWebSocket.subscribeToTyping(chat.id);
    // Check online status
    await this.checkUsersOnlineStatus();
    if (otherParticipant?.participant?.id) {
      await this.checkUserOnlineStatus(otherParticipant.participant.id);
    }
  }

  private async fetchChatMessages(chatId: string, snippetSendAt?: Date) {
    this.hasMoreMessages.value = true;
    this.hasMoreAfter.value = false;
    const params: Record<string, any> = {};
    if (snippetSendAt) {
      params.afterCursor = new Date(snippetSendAt).getTime();
    } else if (this.isCurrentUserBlocked) {
      const removedAt = this.currentChatMetadata.value?.currentUserRemovedAt;
      if (removedAt) params.cursor = removedAt;
    }
    const response = await this.fetchMessagesPage(chatId, params);
    if (response) {
      this.messageMetadataList.value = this.toMessageMetadataList(response.messages);
      this.hasMoreMessages.value = response.hasMore === true;
      this.hasMoreAfter.value = response.hasMoreAfter === true;
      if (!this.isCurrentUserBlocked)
        await this.markAllAsRead();
      if (!snippetSendAt)
        await this.scrollToBottom();
      this.initialLoadDone.value = true;
    }
  }

  private async fetchMessagesPage(chatId: string, params: Record<string, any>, noLoadingIndicator = false): Promise<ChatMessagePageResponse | null> {
    params.pageSize = ChatPresenter.PAGE_SIZE;
    const response: ChatMessagePageResponse = await NamaCommon.namaFetch(
      NamaURLS.CHAT_CONVERSATION + `/${chatId}/messages/page`, {
        method: "GET", params, doNotUseMainReqForHttp: true, noLoadingIndicator
      });
    return (response && NamaCommon.isSuccess(response)) ? response : null;
  }

  private toMessageMetadataList(messages?: DTOChatMessage[]): MessageMetadata[] {
    let filtered = this.filterMessagesForBlockedUser(messages ?? []);
    return filtered.map((msg) => this.createMessageMetadataFromDTO(msg));
  }

  private filterMessagesForBlockedUser(messages: DTOChatMessage[]): DTOChatMessage[] {
    const removedAt = this.currentChatMetadata.value?.currentUserRemovedAt;
    if (!this.isCurrentUserBlocked || !removedAt)
      return messages;
    const removedTime = new Date(removedAt).getTime();
    return messages.filter(msg => !msg.sentAt || new Date(msg.sentAt).getTime() <= removedTime);
  }

  async loadOlderMessages(): Promise<boolean> {
    if (!this.currentChatMetadata.value?.id || !this.hasMoreMessages.value || this.isLoadingMore.value)
      return false;
    const oldestMessage = this.messageMetadataList.value[0];
    if (!oldestMessage?.sentAt)
      return false;
    this.isLoadingMore.value = true;
    try {
      const response = await this.fetchMessagesPage(this.currentChatMetadata.value.id,
        {cursor: new Date(oldestMessage.sentAt).getTime()}, true);
      if (response) {
        const olderMessages = this.toMessageMetadataList(response.messages);
        if (olderMessages.length > 0)
          this.messageMetadataList.value.unshift(...olderMessages);
        this.hasMoreMessages.value = response.hasMore === true;
        return olderMessages.length > 0;
      }
      this.hasMoreMessages.value = false;
      return false;
    } finally {
      this.isLoadingMore.value = false;
    }
  }

  async loadNewerMessages(): Promise<boolean> {
    if (!this.currentChatMetadata.value?.id || !this.hasMoreAfter.value || this.isLoadingMoreAfter.value)
      return false;
    const newestMessage = this.messageMetadataList.value[this.messageMetadataList.value.length - 1];
    if (!newestMessage?.sentAt)
      return false;
    this.isLoadingMoreAfter.value = true;
    try {
      const response = await this.fetchMessagesPage(this.currentChatMetadata.value.id,
        {afterCursor: new Date(newestMessage.sentAt).getTime() + 1}, true);
      if (response) {
        const newerMessages = this.toMessageMetadataList(response.messages);
        if (newerMessages.length > 0)
          this.messageMetadataList.value.push(...newerMessages);
        this.hasMoreAfter.value = response.hasMoreAfter === true;
        return newerMessages.length > 0;
      }
      this.hasMoreAfter.value = false;
      return false;
    } finally {
      this.isLoadingMoreAfter.value = false;
    }
  }

  private async fetchConversation(conversationId: string): Promise<DTOChatConversation | undefined> {
    let response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${conversationId}`, {
      method: "GET",
      doNotUseMainReqForHttp: true,
      noLoadingIndicator: true
    });
    if (response && NamaCommon.isSuccess(response))
      return response;
    return undefined;
  }

  async fetchChatParticipants(chatId: string | undefined) {
    if (!chatId)
      return [];
    try {
      let response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${chatId}/participants`, {
        method: "GET",
        doNotUseMainReqForHttp: true,
        noLoadingIndicator: true
      });
      if (response && NamaCommon.isSuccess(response) && response.length > 0) {
        return response;
      }
    } catch (error) {
      console.error('Failed to fetch participants for chat:', chatId, error);
    }
    return [];
  }

  private fetchChatImage(chat: DTOChatConversation, chatParticipants?: DTOChatParticipant[]): string {
    if (chat.chatConversationType === 'Direct' && chatParticipants) {
      let participants = chatParticipants.filter(p => p.participant?.id !== this.currentUserInfo?.id);
      if (participants && participants.length > 0) {
        return LoginUtils.getUserImageSrc(participants[0].participant?.id ?? '', false);
      }
    }
    return '';
  }

  private fetchChatTitle(chat: DTOChatConversation, chatParticipants?: DTOChatParticipant[]): string {
    if (chat.groupTitle)
      return chat.groupTitle;
    return this.fetchChatParticipantsAsTitle(chatParticipants, chat);
  }

  private fetchChatParticipantsAsTitle(chatParticipants: namaTypes.DTOChatParticipant[] | undefined, chat: namaTypes.DTOChatConversation) {
    if (chatParticipants) {
      let participants = chatParticipants.filter(p => p.participant?.id !== this.currentUserInfo?.id);
      if (chat.chatConversationType === 'Direct') {
        if (participants && participants.length > 0)
          return Translator.translateRef(participants[0].participant);
      } else {
        return participants.map(p => " " + Translator.translateRef(p.participant).split(" ")[0]).join();
      }
    }
    return '';
  }

  findUserById(userId: string): EntityReferenceData | undefined {
    for (const chat of this.chatMetadataList.value) {
      const match = chat.participants?.find(p => p.participant?.id === userId);
      if (match?.participant) return match.participant;
    }
    return undefined;
  }

  async fetchUsers(): Promise<EntityReferenceData[]> {
    let response = await NamaCommon.namaFetch(NamaURLS.ACTIVE_USERS, {
      method: "GET",
      doNotUseMainReqForHttp: true
    });
    if (response && NamaCommon.isSuccess(response))
      return response;
    return [];
  }

  async searchMessages(query: string): Promise<ChatMessageSearchResult[]> {
    const response = await NamaCommon.namaFetch(NamaURLS.CHAT_SEARCH, {
      method: "GET",
      params: {userId: this.currentUserInfo?.id, userType: 'user', query, maxResults: 20},
      doNotUseMainReqForHttp: true,
      noLoadingIndicator: true,
      doNotPushErrorWhenFailure: true
    });
    if (response && NamaCommon.isSuccess(response))
      return response;
    return [];
  }

  async createConversationAndOpen(type: string, users: DTOUser[], groupTitle?: string) {
    this.isCreating.value = true;
    if (!this.currentUserInfo) {
      useErrorsStore().pushErrorStr('cannot find current user');
      return;
    }
    let participants: DTOChatParticipant[] = users.map(user => {
      return {participant: ChatPresenter.createEntityReferenceData(user)}
    });
    try {
      participants.push({
        participant: ChatPresenter.createEntityReferenceData(this.currentUserInfo)
      });
      const conversationRequest: ChatConversationRequest = {
        chatConversationType: type,
        participants: participants,
        groupTitle: groupTitle
      };
      let response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION, {
        method: "POST",
        data: conversationRequest,
        doNotUseMainReqForHttp: true
      });
      if (response && NamaCommon.isSuccess(response)) {
        this.addConversationToList(response, participants);
        await this.fetchChatAndOpen(response);
      }
    } catch (error: any) {
      useErrorsStore().pushErrorStr(error.message);
    } finally {
      this.isCreating.value = false;
    }
  }

  async updateGroupTitle(newTitle: string): Promise<boolean> {
    const chatId = this.currentChatMetadata.value?.id;
    if (!chatId) return false;
    try {
      const response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${chatId}/title`, {
        method: "PUT",
        data: {groupTitle: newTitle},
        doNotUseMainReqForHttp: true
      });
      if (response && NamaCommon.isSuccess(response)) {
        this.currentChatMetadata.value!.groupTitle = newTitle;
        this.currentChatMetadata.value!.chatTitle = newTitle;
        const listItem = this.chatMetadataList.value.find(c => c.id === chatId);
        if (listItem) {
          listItem.groupTitle = newTitle;
          listItem.chatTitle = newTitle;
        }
        return true;
      }
    } catch (error: any) {
      useErrorsStore().pushErrorStr(error.message);
    }
    return false;
  }

  async addParticipant(user: EntityReferenceData): Promise<boolean> {
    const chatId = this.currentChatMetadata.value?.id;
    if (!chatId) return false;
    try {
      const response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${chatId}/participants`, {
        method: "POST",
        data: {participant: user},
        doNotUseMainReqForHttp: true
      });
      if (response && NamaCommon.isSuccess(response)) {
        this.currentChatMetadata.value!.participants = await this.fetchChatParticipants(chatId);
        this.updateParticipantsTitleAndCount();
        return true;
      }
    } catch (error: any) {
      useErrorsStore().pushErrorStr(error.message);
    }
    return false;
  }

  async removeParticipant(user: EntityReferenceData): Promise<boolean> {
    const chatId = this.currentChatMetadata.value?.id;
    if (!chatId) return false;
    try {
      const response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${chatId}/participants`, {
        method: "DELETE",
        data: {participant: user},
        doNotUseMainReqForHttp: true
      });
      if (!response || NamaCommon.isSuccess(response)) {
        this.currentChatMetadata.value!.participants = await this.fetchChatParticipants(chatId);
        this.updateParticipantsTitleAndCount();
        return true;
      }
    } catch (error: any) {
      useErrorsStore().pushErrorStr(error.message);
    }
    return false;
  }

  private updateParticipantsTitleAndCount() {
    const meta = this.currentChatMetadata.value;
    if (!meta) return;
    meta.chatParticipantsTitle = this.fetchChatParticipantsAsTitle(meta.participants, meta);
    meta.participantCount = meta.participants?.length;
    const listItem = this.chatMetadataList.value.find(c => c.id === meta.id);
    if (listItem) {
      listItem.chatParticipantsTitle = meta.chatParticipantsTitle;
      listItem.participantCount = meta.participantCount;
    }
  }

  async makeParticipantAdmin(user: EntityReferenceData): Promise<boolean> {
    const chatId = this.currentChatMetadata.value?.id;
    if (!chatId) return false;
    try {
      const response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${chatId}/participants/admin`, {
        method: "PUT",
        data: {participant: user},
        doNotUseMainReqForHttp: true
      });
      if (response && NamaCommon.isSuccess(response)) {
        this.currentChatMetadata.value!.participants = await this.fetchChatParticipants(chatId);
        useMessagesStore().pushMessageStr(Translator.translateId('chatMakeAdmin') + ' ' + Translator.translateRef(user));
        return true;
      }
    } catch (error: any) {
      useErrorsStore().pushErrorStr(error.message);
    }
    return false;
  }

  async removeParticipantAdmin(user: EntityReferenceData): Promise<boolean> {
    const chatId = this.currentChatMetadata.value?.id;
    if (!chatId) return false;
    try {
      const response = await NamaCommon.namaFetch(NamaURLS.CHAT_CONVERSATION + `/${chatId}/participants/admin`, {
        method: "DELETE",
        data: {participant: user},
        doNotUseMainReqForHttp: true
      });
      if (response && NamaCommon.isSuccess(response)) {
        this.currentChatMetadata.value!.participants = await this.fetchChatParticipants(chatId);
        useMessagesStore().pushMessageStr(Translator.translateId('chatRemoveAdmin') + ' ' + Translator.translateRef(user));
        return true;
      }
    } catch (error: any) {
      useErrorsStore().pushErrorStr(error.message);
    }
    return false;
  }

  isCurrentUserGroupOwner(): boolean {
    const meta = this.currentChatMetadata.value;
    if (!meta || meta.chatConversationType !== 'Group')
      return false;
    return meta.groupOwner?.id === this.currentUserInfo?.id;
  }

  isCurrentUserAdmin(): boolean {
    if (this.isCurrentUserGroupOwner())
      return true;
    const meta = this.currentChatMetadata.value;
    if (!meta || meta.chatConversationType !== 'Group')
      return false;
    const currentParticipant = meta.participants?.find(p => p.participant?.id === this.currentUserInfo?.id);
    return currentParticipant?.role === 'Admin';
  }

  get isCurrentUserParticipant(): boolean {
    const meta = this.currentChatMetadata.value;
    if (!meta) return false;
    if (meta.chatConversationType !== 'Group')
      return true;
    if (meta.currentUserRole === 'Blocked')
      return false;
    return !!meta.participants?.some(p => p.participant?.id === this.currentUserInfo?.id);
  }

  get isCurrentUserBlocked(): boolean {
    const meta = this.currentChatMetadata.value;
    if (!meta || meta.chatConversationType !== 'Group')
      return false;
    return meta.currentUserRole === 'Blocked';
  }

  private addConversationToList(chat: DTOChatConversation, participants: DTOChatParticipant[]) {
    if (!chat.id || this.chatMetadataList.value.some(c => c.id === chat.id))
      return;
    this.chatMetadataList.value.unshift(this.buildChatMetadata(chat, participants));
  }

  private async ensureConversationInList(conversationId: string | undefined) {
    if (!conversationId || this.chatMetadataList.value.some(c => c.id === conversationId))
      return;
    const chat = await this.fetchConversation(conversationId);
    if (!chat || !chat.id)
      return;
    const participants = await this.fetchChatParticipants(chat.id);
    this.addConversationToList(chat, participants);
  }

  static createEntityReferenceData(obj: any) {
    return {
      id: obj.id,
      entityType: obj.entityType,
      code: obj.code,
      actualCode: obj.actualCode,
      altCode: obj.altCode,
      name1: obj.name1,
      name2: obj.name2
    }
  }

  onPartialUserInput(value: any) {
    this.messageText.value = value ?? '';
    this.addToDraftMessages(value);
    this.startTyping();
  }

  private removeFromDraftMessages() {
    if (this.currentChatMetadata.value?.id)
      delete this.draftMessages[this.currentChatMetadata.value?.id];
  }

  private addToDraftMessages(value: any) {
    if (this.currentChatMetadata.value?.id)
      this.draftMessages[this.currentChatMetadata.value?.id] = value;
  }

  private getDraftMessage(chatId: string | undefined): string {
    if (chatId)
      return this.draftMessages[chatId] ?? '';
    return '';
  }

  private saveCurrentDraft() {
    if (this.currentChatMetadata.value?.id) {
      const currentText = this.messageText.value?.trim();
      if (currentText) {
        this.addToDraftMessages(this.messageText.value);
      } else {
        this.removeFromDraftMessages();
      }
    }
  }

  private startTyping() {
    if (!this.currentChatMetadata.value?.id || !namaWebSocket.isConnected())
      return;
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }
    namaWebSocket.sendTyping(this.currentChatMetadata.value.id, true);
    this.typingTimeout = setTimeout(() => this.stopTyping(), 1500)
  }

  private async handleIncomingMessage(message: MessageMetadata) {
    const messageId = message.id;
    if (messageId && this.processedMessageIds.has(messageId)) {
      return;
    }
    if (messageId) {
      this.processedMessageIds.add(messageId);
      if (this.processedMessageIds.size > 500) {
        const idsArray = Array.from(this.processedMessageIds);
        this.processedMessageIds = new Set(idsArray.slice(-250));
      }
    }
    if (message.isSystemMessage) {
      await this.handleSystemMessage(message);
      return;
    }
    if (this.isCurrentUserBlocked && message.conversationId === this.currentChatMetadata.value?.id)
      return;
    const isFromOtherUser = message.sender?.id !== this.currentUserInfo?.id;
    if (message.conversationId === this.currentChatMetadata.value?.id) {
      if (message.sender?.id === this.currentUserInfo?.id) {
        const tempIndex = this.findTemporaryMessageIndex(message);
        if (tempIndex !== -1) {
          this.messageMetadataList.value[tempIndex] = message;
          await this.scrollToBottom();
          return;
        }
      }
      const existingIndex = this.messageMetadataList.value.findIndex(m => m.id === message.id);
      if (existingIndex !== -1) {
        this.messageMetadataList.value[existingIndex] = {...this.messageMetadataList.value[existingIndex], ...message};
      } else if (!this.hasMoreAfter.value) {
        this.messageMetadataList.value.push(message);
        if (isFromOtherUser && this.isChatOpened()) {
          namaWebSocket.sendDeliveryReceipt(message.id!);
          namaWebSocket.sendReadReceipt(message.id!);
        }
        await this.scrollToBottom();
      }
    } else if (isFromOtherUser) {
      await this.ensureConversationInList(message.conversationId);
      this.incrementUnreadCount(message.conversationId);
    }
    this.updateMetadataLastMessage(message);
    if (isFromOtherUser && namaBroadcast.isLeaderTab()) {
      namaBroadcast.broadcastChatMessage(message, message.conversationId);
    }
  }

  private async handleSystemMessage(message: MessageMetadata) {
    const content = message.messageContent ?? '';
    const parts = content.split(':');
    const action = parts[0];
    const targetUserId = parts[1];
    const isCurrentConversation = message.conversationId === this.currentChatMetadata.value?.id;
    if (action === IdsAndConstants.MEMBER_REMOVED && targetUserId === this.currentUserInfo?.id) {
      const meta = this.currentChatMetadata.value;
      if (meta && meta.id === message.conversationId)
        meta.currentUserRole = 'Blocked';
      const listItem = this.chatMetadataList.value.find(c => c.id === message.conversationId);
      if (listItem)
        listItem.currentUserRole = 'Blocked';
    }
    if (action === IdsAndConstants.MEMBER_ADDED && targetUserId === this.currentUserInfo?.id) {
      const meta = this.currentChatMetadata.value;
      if (meta && meta.id === message.conversationId) {
        meta.currentUserRole = 'Member';
        meta.participants = await this.fetchChatParticipants(meta.id!);
        this.updateParticipantsTitleAndCount();
      }
      const listItem = this.chatMetadataList.value.find(c => c.id === message.conversationId);
      if (listItem)
        listItem.currentUserRole = 'Member';
    }
    if (isCurrentConversation) {
      const existingIndex = this.messageMetadataList.value.findIndex(m => m.id === message.id);
      if (existingIndex === -1 && !this.hasMoreAfter.value) {
        this.messageMetadataList.value.push(message);
        await this.scrollToBottom();
      }
      if (action === IdsAndConstants.MEMBER_ADDED || action === IdsAndConstants.MEMBER_REMOVED) {
        if (this.currentChatMetadata.value?.id) {
          this.currentChatMetadata.value.participants = await this.fetchChatParticipants(this.currentChatMetadata.value.id);
          this.updateParticipantsTitleAndCount();
        }
      }
    }
  }

  private incrementUnreadCount(conversationId: string | undefined) {
    if (!conversationId)
      return;
    const chatMetadata = this.chatMetadataList.value.find(c => c.id === conversationId);
    if (chatMetadata) {
      chatMetadata.unreadMessagesCount = (chatMetadata.unreadMessagesCount ?? 0) + 1;
      const totalUnread = this.getTotalUnreadCount();
      namaBroadcast.broadcastChatUnreadCount(conversationId, chatMetadata.unreadMessagesCount, totalUnread);
    }
  }

  /**
   * Calculate total unread messages across all conversations
   */
  private getTotalUnreadCount(): number {
    return this.chatMetadataList.value.reduce((total, chat) => {
      return total + (chat.unreadMessagesCount ?? 0);
    }, 0);
  }

  findTemporaryMessageIndex(message: MessageMetadata): number {
    return this.messageMetadataList.value.findIndex(m => {
      if (!m.isTemporary)
        return false;
      return message.id && m.id === message.id;
    });
  }

  private handleMessageStatusUpdate(statusUpdate: MessageStatusUpdate) {
    const messageIndex = this.messageMetadataList.value.findIndex(m => m.id === statusUpdate.messageId);
    if (messageIndex !== -1) {
      const updatedMessage: MessageMetadata = {
        ...this.messageMetadataList.value[messageIndex],
        status: statusUpdate.status,
        ...(statusUpdate.readAt && {readAt: new Date(statusUpdate.readAt)}),
        ...(statusUpdate.deliveredAt && {deliveredAt: new Date(statusUpdate.deliveredAt)})
      };
      // Set timestamp fields based on status if not provided
      if (statusUpdate.status === ChatMessageStatus.Read && !updatedMessage.readAt) {
        updatedMessage.readAt = new Date();
      }
      if (statusUpdate.status === ChatMessageStatus.Delivered && !updatedMessage.deliveredAt) {
        updatedMessage.deliveredAt = new Date();
      }
      this.messageMetadataList.value[messageIndex] = updatedMessage;
    } else {
      console.warn('Status update for unknown message:', statusUpdate.messageId);
    }
  }

  async markAllAsRead() {
    if (!this.currentChatMetadata.value?.id) return;
    try {
      const unreadMessages = this.messageMetadataList.value.filter(
        msg => msg.sender?.id !== this.currentUserInfo?.id &&
          msg.status !== ChatMessageStatus.Read &&
          !msg.readAt
      );
      for (const msg of unreadMessages) {
        if (msg.id && namaWebSocket.isConnected()) {
          namaWebSocket.sendReadReceipt(msg.id);
        }
      }
      let req: MarkConversationReadRequest = {
        conversationId: this.currentChatMetadata.value.id,
        userId: this.currentUserInfo?.id
      };
      await NamaCommon.namaFetch(NamaURLS.MARK_READ,
        {
          method: 'POST',
          data: req,
          doNotUseMainReqForHttp: true,
          noLoadingIndicator: true
        }
      );
      this.resetUnreadCount(this.currentChatMetadata.value.id);
      this.unreadCount.value = 0;
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }

  private updateMetadataLastMessage(msgBody: DTOChatMessage | undefined) {
    if (!msgBody)
      return;
    const chatMetadata = this.chatMetadataList.value.find(c => c.id === msgBody.conversationId);
    if (chatMetadata) {
      chatMetadata.lastMessagePreview = ChatUtils.getMessagePreviewText(msgBody);
      chatMetadata.lastMessageType = msgBody.messageType;
      chatMetadata.lastMessageAt = msgBody.sentAt;
    }
  }

  private async checkUsersOnlineStatus() {
    try {
      const participantIds = this.currentChatMetadata.value?.participants?.map(
        (l) => l.participant?.id ?? ''
      ).filter(id => id) || [];
      if (participantIds.length === 0) return;
      const response = await NamaCommon.namaFetch(NamaURLS.ONLINE_USERS + `/batch`, {
        method: 'POST',
        doNotUseMainReqForHttp: true,
        noLoadingIndicator: true,
        data: JSON.stringify(participantIds)
      });
      if (response) {
        for (const [userId, isOnline] of Object.entries(response)) {
          this.onlineUsersMap.value.set(userId, isOnline as boolean);
        }
      }
    } catch (error) {
      console.error('Failed to check online status:', error)
    }
  }

  async checkUserOnlineStatus(userId: string): Promise<boolean> {
    try {
      const response = await NamaCommon.namaFetch(`${NamaURLS.ONLINE_USERS}/${userId}`, {
        method: 'GET',
        doNotUseMainReqForHttp: true,
      });
      if (typeof response === 'boolean') {
        this.onlineUsersMap.value.set(userId, response);
        return response;
      }
      return false;
    } catch (error) {
      console.error('Failed to check user online status:', error);
      return false;
    }
  }

  private handlePresenceUpdate(presenceUpdate: PresenceUpdate) {
    this.onlineUsersMap.value.set(presenceUpdate.userId, presenceUpdate.online);
    namaBroadcast.broadcastPresence(presenceUpdate);
  }

  private handleTypingIndicator(indicator: TypingIndicatorDTO) {
    if (indicator.userId !== this.currentUserInfo?.id)
      this.otherUserTyping.value = indicator.typing ?? false;
  }

  async sendMessage(messageContent?: string, attachment?: DTOLargeData) {
    const content = messageContent || this.messageText.value;
    const messageType: string = attachment ? ChatUtils.getMessageTypeForFile(attachment.attachmentInfo?.fileName) : 'Text';
    if (!namaWebSocket.isConnected()) {
      useErrorsStore().pushErrorStr('WebSocket not connected!');
      return;
    }
    if (!content?.trim() && !attachment)
      return;
    if (!this.currentChatMetadata.value?.id) {
      useErrorsStore().pushErrorStr('No conversation ID! Create a conversation first.');
      return;
    }
    if (this.hasMoreAfter.value)
      await this.fetchChatMessages(this.currentChatMetadata.value.id);
    const id: string = await ClientCache.asyncNextGUIDId();
    const tempMessage: MessageMetadata = {
      id: id,
      conversationId: this.currentChatMetadata.value.id,
      sender: {
        id: this.currentUserInfo?.id,
        entityType: this.currentUserInfo?.entityType
      },
      messageContent: content || attachment?.attachmentInfo?.fileName || '',
      messageType: messageType,
      attachment: attachment,
      status: ChatMessageStatus.Pending,
      sentAt: new Date(),
      isTemporary: true
    };
    // Add to local messages immediately for optimistic UI
    this.messageMetadataList.value.push(tempMessage);
    this.currentChatMetadata.value.lastMessagePreview = ChatUtils.getMessagePreviewText(tempMessage);
    this.currentChatMetadata.value.lastMessageType = messageType;
    await this.scrollToBottom();
    namaWebSocket.sendMessage(this.currentChatMetadata.value.id, content || '', messageType, attachment, id);
    this.messageText.value = '';
    this.removeFromDraftMessages();
    // Stop typing indicator when message is sent
    this.stopTyping()
  }

  async sendMessageWithAttachments(attachments: ChatAttachment[]) {
    if (!attachments.length) return;
    // Send first attachment with the caption as message content
    const first = attachments[0];
    const firstAttachment: DTOLargeData = {
      attachmentInfo: {fileId: first.fileId, fileName: first.fileName, updated: true, removed: false},
      dataLength: first.fileSize
    };
    await this.sendMessage(this.messageText.value || '', firstAttachment);
    for (let i = 1; i < attachments.length; i++) {
      const att = attachments[i];
      const attachment: DTOLargeData = {
        attachmentInfo: {fileId: att.fileId, fileName: att.fileName, updated: true, removed: false},
        dataLength: att.fileSize
      };
      await this.sendMessage('', attachment);
    }
  }

  stopTyping() {
    if (!this.currentChatMetadata.value?.id || !namaWebSocket.isConnected())
      return;
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
      this.typingTimeout = null
    }
    namaWebSocket.sendTyping(this.currentChatMetadata.value.id, false);
  }

  async scrollToBottom(): Promise<void> {
    this.isAutoScrolling.value = true;
    return nextTick().then(() => nextTick()).then(() => {
      return new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          const el = this.chatContainerRef.value;
          if (el) {
            el.scrollTop = el.scrollHeight;
          }
          this.isAutoScrolling.value = false;
          resolve();
        });
      });
    });
  }

  setChatContainerRef(containerRef: Ref<HTMLElement | null>) {
    this.chatContainerRef = containerRef;
  }

  destroy() {
    this.stopTyping();
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    // Unsubscribe from typing indicators
    if (this.currentChatMetadata.value?.id) {
      namaWebSocket.unsubscribeFromTyping(this.currentChatMetadata.value.id);
    }
    this.wsUnsubscribers.forEach(unsub => unsub());
    this.wsUnsubscribers = [];
    this.broadcastUnsubscribers.forEach(unsub => unsub());
    this.broadcastUnsubscribers = [];
    this.processedMessageIds.clear();
    this.initialLoadDone.value = false;
    this.messageMetadataList.value = [];
    this.chatMetadataList.value = [];
    this.draftMessages = {};
  }

  isUserOnline(userId: string): boolean {
    return this.onlineUsersMap.value.get(userId) || false;
  }

  private createMessageMetadataFromDTO(msg: DTOChatMessage): MessageMetadata {
    let messageMetadata: MessageMetadata = msg;
    messageMetadata.isOwn = msg.sender?.id === this.currentUserInfo?.id;
    messageMetadata.senderName = msg.sender?.id === this.currentUserInfo?.id ? Translator.translateId('You') : this.getOtherUserName(msg.sender?.id);
    messageMetadata.formattedTime = msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '';
    messageMetadata.actualStatus = this.normalizeMessageStatus(msg);
    return messageMetadata;
  }

  private normalizeMessageStatus(msg: MessageMetadata): string {
    if (msg.readAt)
      return ChatMessageStatus.Read;
    if (msg.deliveredAt)
      return ChatMessageStatus.Delivered;
    return msg.status || ChatMessageStatus.Pending;
  }

  private getOtherUserName(senderId: string | undefined): string {
    if (!senderId)
      return '';
    const otherParticipant = this.currentChatMetadata.value?.participants?.find(
      p => p.participant?.id === senderId
    );
    return Translator.translateRef(otherParticipant?.participant) || '';
  }

  getFileDownloadUrl(message: MessageMetadata): string {
    return ChatUtils.getFileDownloadUrl(message.attachment?.attachmentInfo?.fileId);
  }

  private isChatOpened(): boolean {
    return !!this.currentChatMetadata.value?.id;
  }

  groupMembers(): any[] {
    const meta = this.currentChatMetadata.value;
    if (!meta || meta.chatConversationType !== 'Group') return [];
    const participants = meta.participants ?? [];
    return participants
      .filter(p => p.participant?.id !== this.currentUserInfo?.id && meta.currentUserRole !== 'Blocked')
      .map(p => p.participant)
      .filter(Boolean);
  }

  get mentionPhase(): 'member' | 'entity' | 'record' | null {
    if (!this.mentionActive.value)
      return null;
    if (this.mentionEntityId.value)
      return 'record';
    if (this.mentionQuery.value.includes(':'))
      return 'entity';
    return 'member';
  }

  checkMention(editableEl: HTMLElement): void {
    if (this.mentionEscaped) return;
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) {
      this.deactivateMention();
      return;
    }
    const anchorNode = sel.anchorNode;
    const anchorOffset = sel.anchorOffset;
    if (!anchorNode || !editableEl.contains(anchorNode)) {
      this.deactivateMention();
      return;
    }
    if (anchorNode.nodeType !== Node.TEXT_NODE) {
      this.deactivateMention();
      return;
    }
    // Stage 2: cursor is in text node near an entity prefix span
    const prefixEl = this.findEntityPrefix(anchorNode.previousSibling);
    if (prefixEl) {
      const entityId = prefixEl.dataset.entityId ?? '';
      const textBeforeCursor = (anchorNode.textContent || '').substring(0, anchorOffset);
      const range = document.createRange();
      range.setStartBefore(prefixEl);
      range.setEnd(anchorNode, anchorOffset);
      this.mentionActive.value = true;
      this.mentionQuery.value = textBeforeCursor;
      this.mentionEntityId.value = entityId;
      this.mentionRange.value = range;
      return;
    }
    // Stage 1: walk backward to find bare '@'
    this.mentionEntityId.value = null;
    const text = anchorNode.textContent || '';
    const textBeforeCursor = text.substring(0, anchorOffset);
    let atIndex = -1;
    for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
      const ch = textBeforeCursor[i];
      if (ch === '@') {
        atIndex = i;
        break;
      }
      if (ch === ' ' || ch === '\u00A0' || ch === '\n' || ch === '\r' || ch === '\t') {
        break;
      }
    }
    if (atIndex === -1) {
      this.deactivateMention();
      return;
    }
    if (atIndex > 0) {
      const charBefore = textBeforeCursor[atIndex - 1];
      if (charBefore !== ' ' && charBefore !== '\u00A0' && charBefore !== '\n' && charBefore !== '\r' && charBefore !== '\t') {
        this.deactivateMention();
        return;
      }
    }
    const rawQuery = textBeforeCursor.substring(atIndex + 1);
    // Text-based Stage 2: ":" is a deterministic switch to server search mode
    const colonIndex = rawQuery.indexOf(':');
    if (colonIndex > 0) {
      const entityLabel = rawQuery.substring(0, colonIndex);
      const range = document.createRange();
      range.setStart(anchorNode, atIndex);
      range.setEnd(anchorNode, anchorOffset);
      this.mentionActive.value = true;
      this.mentionQuery.value = rawQuery.substring(colonIndex + 1);
      this.mentionEntityId.value = entityLabel;
      this.mentionRange.value = range;
      return;
    }
    // Stage 1: entity search
    const range = document.createRange();
    range.setStart(anchorNode, atIndex);
    range.setEnd(anchorNode, anchorOffset);
    this.mentionActive.value = true;
    this.mentionQuery.value = rawQuery;
    this.mentionRange.value = range;
  }

  insertUserMention(editableEl: HTMLElement, user: SearchItem): void {
    const displayName = user.name1 || user.name2 || user.code || user.id;
    this.insertMentionChip(editableEl, '@' + displayName, {id: user.id ?? '', link: 'user:' + (user.id ?? '')});
  }

  insertEntityPrefix(editableEl: HTMLElement, entity: SearchItem): void {
    if (!this.mentionRange.value) return;
    const sel = window.getSelection();
    if (!sel) return;
    this.mentionRange.value.deleteContents();
    const prefix = document.createElement('span');
    prefix.contentEditable = 'false';
    prefix.className = 'mention-entity-prefix';
    prefix.dataset.entityId = entity.id ?? '';
    prefix.textContent = '@' + entity.text + ': ';
    this.mentionRange.value.insertNode(prefix);
    const textNode = document.createTextNode('');
    prefix.after(textNode);
    const newRange = document.createRange();
    newRange.setStart(textNode, 0);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    this.mentionActive.value = true;
    this.mentionEntityId.value = entity.id ?? null;
    this.mentionQuery.value = '';
    this.mentionRange.value = null;
    editableEl.dispatchEvent(new Event('input', {bubbles: true}));
  }

  insertMention(editableEl: HTMLElement, item: SearchItem): void {
    const label = this.buildMentionLabel(item);
    const href = ChatUtils.buildMentionHref(item);
    this.insertMentionChip(editableEl, '@' + label, {id: item.record?.id ?? item.id ?? '', link: href});
  }

  private insertMentionChip(editableEl: HTMLElement, text: string, dataset: Record<string, string>): void {
    if (!this.mentionRange.value)
      return;
    const sel = window.getSelection();
    if (!sel)
      return;
    this.mentionRange.value.deleteContents();
    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.className = 'mention-chip';
    Object.assign(chip.dataset, dataset);
    chip.textContent = text;
    this.mentionRange.value.insertNode(chip);
    const space = document.createTextNode('\u00A0');
    chip.after(space);
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    this.deactivateMention();
    editableEl.dispatchEvent(new Event('input', {bubbles: true}));
  }

  cancelMention(editableEl: HTMLElement): void {
    const prefixEl = editableEl.querySelector('.mention-entity-prefix');
    if (prefixEl) {
      prefixEl.remove();
      editableEl.normalize();
    }
    this.mentionEscaped = true;
    this.deactivateMention();
    editableEl.dispatchEvent(new Event('input', {bubbles: true}));
  }

  resetEscaped(): void {
    this.mentionEscaped = false;
  }

  private deactivateMention(): void {
    this.mentionActive.value = false;
    this.mentionQuery.value = '';
    this.mentionEntityId.value = null;
    this.mentionRange.value = null;
  }

  private findEntityPrefix(startNode: Node | null): HTMLElement | null {
    let node = startNode;
    while (node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        return el.classList.contains('mention-entity-prefix') ? el : null;
      }
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.length) {
        return null;
      }
      node = node.previousSibling;
    }
    return null;
  }

  private buildMentionLabel(item: SearchItem): string {
    if (item.record)
      return Translator.translateId(item.record.entityType) + ': ' + Translator.translateRef(item.record);
    return item.text;
  }
}
